import { formatDuration, useTicker } from "@savvy/runtime";
import { Badge, Button, cn } from "@savvy/ui";
import { useEffect, useRef, useState } from "react";

import { useLive } from "../state/liveStore";

/**
 * The wallboard — the Phase 4 gate's "live wallboard". A TV surface: eight
 * tiles, big numbers, warn/alert borders, no interaction beyond fullscreen.
 *
 * Every number derives from the live store at render; the only timer is the
 * shared 1 Hz ticker driving the clock and the longest-wait counter.
 */

interface Tile {
  label: string;
  value: string;
  tone?: "ok" | "warn" | "bad";
}

export function WallboardView() {
  const now = useTicker();
  const calls = useLive((s) => s.calls);
  const agents = useLive((s) => s.agents);
  const conn = useLive((s) => s.conn);
  const partial = useLive((s) => s.partial);

  const ref = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState(false);

  useEffect(() => {
    const onChange = () => setFull(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const provisioned = [...agents.values()].filter((a) => a.provisioned);
  const byStatus = (s: string) => provisioned.filter((a) => a.status === s).length;
  const inCall = provisioned.filter(
    (a) => a.state === "In a queue call" || a.state === "Receiving",
  ).length;
  const ringing = provisioned.filter((a) => a.state === "Receiving").length;

  const inbound = [...calls.values()].filter((c) => c.direction !== "outbound");
  const active = inbound.filter((c) => c.bridged);
  const waiting = inbound.filter((c) => !c.bridged);
  const longestWaitSec = waiting.reduce(
    (max, c) => Math.max(max, Math.floor((now - c.seenAt) / 1000)),
    0,
  );

  const agentsUnknown = partial.includes("orchestrator");
  const callsUnknown = partial.includes("callctl");
  const unknown = "—";

  const tiles: Tile[] = [
    { label: "Agents", value: agentsUnknown ? unknown : String(provisioned.length) },
    {
      label: "Ready",
      value: agentsUnknown ? unknown : String(byStatus("Available")),
      tone: !agentsUnknown && byStatus("Available") === 0 ? "bad" : "ok",
    },
    { label: "On Call", value: agentsUnknown ? unknown : String(inCall) },
    { label: "Ringing", value: agentsUnknown ? unknown : String(ringing) },
    {
      label: "On Break",
      value: agentsUnknown ? unknown : String(byStatus("On Break")),
      tone: !agentsUnknown && byStatus("On Break") > 0 ? "warn" : undefined,
    },
    { label: "Active Calls", value: callsUnknown ? unknown : String(active.length) },
    {
      label: "Queued",
      value: callsUnknown ? unknown : String(waiting.length),
      tone: callsUnknown ? undefined : waiting.length >= 5 ? "bad" : waiting.length > 0 ? "warn" : undefined,
    },
    {
      label: "Longest Wait",
      value: callsUnknown ? unknown : formatDuration(longestWaitSec),
      tone: callsUnknown ? undefined : longestWaitSec > 120 ? "bad" : longestWaitSec > 60 ? "warn" : undefined,
    },
  ];

  return (
    <div ref={ref} className={cn("bg-background", full && "p-8")}>
      <div className="mb-4 flex items-center gap-3">
        <span className="mr-auto font-mono text-2xl tabular-nums">
          {new Date(now).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
        </span>
        {conn !== "live" && (
          <Badge variant="danger" className="px-3 py-1 font-bold">
            {conn.toUpperCase()}
          </Badge>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (document.fullscreenElement) void document.exitFullscreen();
            else void ref.current?.requestFullscreen();
          }}
        >
          {full ? "exit fullscreen" : "fullscreen"}
        </Button>
      </div>
      <div className={cn("grid grid-cols-4 gap-3", full && "h-[70vh]")}>
        {tiles.map((t) => (
          <div
            key={t.label}
            className={cn(
              "flex flex-col items-center justify-center gap-2 rounded-md border-2 bg-card p-6 text-card-foreground",
              t.tone === "warn" && "border-warning bg-warning-bg text-warning",
              t.tone === "bad" && "border-danger bg-danger-bg text-danger",
            )}
          >
            <span className={cn("font-mono font-bold tabular-nums", full ? "text-8xl" : "text-5xl")}>
              {t.value}
            </span>
            <span className="text-xs uppercase text-muted-foreground">{t.label}</span>
          </div>
        ))}
      </div>
      {(agentsUnknown || callsUnknown) && (
        <p className="mt-3 text-sm text-muted-foreground">
          Some sections could not be read at connect ({[...(agentsUnknown ? ["agents"] : []), ...(callsUnknown ? ["calls"] : [])].join(", ")}) — shown as {unknown} rather than zero.
        </p>
      )}
    </div>
  );
}
